import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState, ListingLinkButton, Panel, ScoreBadge } from "@/components/common/Primitives";
import { api, apiError } from "@/lib/api";
import { countryName, eur, eurSigned, km, pct, regYear } from "@/lib/format";

const inputCls =
  "h-7 w-full rounded-sm border border-slate-300 bg-white px-1.5 text-[12px] outline-none focus:border-blue-500";

export default function Watchlist() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/watchlist");
      setItems(data.items);
      setStatuses(data.statuses);
    } catch (e) {
      console.error("Watchlist laden mislukt:", e);
      setError(apiError(e));
    }
    setLoading(false);
    // api/apiError are stable module singletons and state setters are stable,
    // so this callback intentionally has no dependencies.
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (listingId, changes) => {
    try {
      await api.put(`/watchlist/${listingId}`, changes);
      load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  return (
    <AppShell title="Watchlist" subtitle="Opgeslagen voertuigen, doelprijzen en onderhandelingsstatus">
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      )}
      <Panel testid="watchlist-panel">
        {loading ? (
          <div className="px-4 py-10 text-center text-[13px] text-slate-500">Laden…</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Nog geen voertuigen op de watchlist"
            description="Open een handelskans en gebruik 'Aan watchlist' om deze hier te volgen met doelinkoopprijs, notities en onderhandelingsstatus."
            testid="watchlist-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Voertuig</th>
                  <th>Land</th>
                  <th>Vraagprijs</th>
                  <th>Bijdrage</th>
                  <th>ROI</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Doel inkoop</th>
                  <th>Max inkoop</th>
                  <th>Contact</th>
                  <th>Onderhandeling</th>
                  <th>Rating</th>
                  <th>Notities</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const l = item.listing || {};
                  const h = item.headline || {};
                  return (
                    <tr key={item.id} data-testid={`watchlist-row-${item.listing_id}`}>
                      <td className="max-w-[220px]">
                        <button
                          type="button"
                          onClick={() => navigate(`/auto/${item.listing_id}`)}
                          className="text-left"
                        >
                          <div className="truncate text-[12.5px] font-semibold text-slate-900">
                            {[l.make, l.model, l.variant].filter(Boolean).join(" ")}
                          </div>
                          <div className="truncate text-[10.5px] text-slate-500">
                            {[regYear(l.first_registration), km(l.mileage_km)].join(" | ")}
                          </div>
                        </button>
                      </td>
                      <td className="text-[12px]">{countryName(l.country)}</td>
                      <td className="num">{eur(l.price)}</td>
                      <td
                        className={`num font-semibold ${
                          (h.contribution ?? 0) > 0 ? "text-emerald-700" : "text-red-700"
                        }`}
                      >
                        {eurSigned(h.contribution, { fallback: "—" })}
                      </td>
                      <td className="num">{pct(h.roi_pct, { fallback: "—" })}</td>
                      <td>
                        <ScoreBadge score={h.deal_score} advice={h.advice} />
                      </td>
                      <td>
                        <select
                          value={item.status || "Nieuw"}
                          data-testid={`watchlist-status-${item.listing_id}`}
                          onChange={(e) => update(item.listing_id, { status: e.target.value })}
                          className={inputCls}
                        >
                          {statuses.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          defaultValue={item.target_buy_price ?? ""}
                          data-testid={`watchlist-target-${item.listing_id}`}
                          onBlur={(e) =>
                            update(item.listing_id, {
                              target_buy_price: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          className={`${inputCls} w-24`}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          defaultValue={item.max_purchase_price ?? ""}
                          onBlur={(e) =>
                            update(item.listing_id, {
                              max_purchase_price:
                                e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          className={`${inputCls} w-24`}
                        />
                      </td>
                      <td>
                        <label className="flex items-center gap-1 text-[11px]">
                          <input
                            type="checkbox"
                            checked={!!item.seller_contacted}
                            onChange={(e) =>
                              update(item.listing_id, { seller_contacted: e.target.checked })
                            }
                            className="h-3 w-3 accent-blue-600"
                          />
                          benaderd
                        </label>
                      </td>
                      <td>
                        <input
                          defaultValue={item.negotiation_status ?? ""}
                          onBlur={(e) =>
                            update(item.listing_id, { negotiation_status: e.target.value })
                          }
                          className={`${inputCls} w-28`}
                        />
                      </td>
                      <td>
                        <select
                          value={item.personal_rating ?? ""}
                          onChange={(e) =>
                            update(item.listing_id, {
                              personal_rating: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          className={`${inputCls} w-14`}
                        >
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          defaultValue={item.notes ?? ""}
                          data-testid={`watchlist-notes-${item.listing_id}`}
                          onBlur={(e) => update(item.listing_id, { notes: e.target.value })}
                          className={`${inputCls} w-40`}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <ListingLinkButton url={l.url} testid={`watchlist-url-${item.listing_id}`} />
                          <button
                            type="button"
                            data-testid={`watchlist-remove-${item.listing_id}`}
                            onClick={async () => {
                              await api.delete(`/watchlist/${item.listing_id}`);
                              load();
                            }}
                            className="rounded-sm border border-slate-300 p-1 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </AppShell>
  );
}
